import { useRef, useState, useEffect } from 'react';
import { Upload, X, Eye, Download, Loader2, FileImage } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Upload as IUpload } from '@/types';
import { useAuth } from '@/providers/AuthProvider';
import { toast } from 'sonner';
import { useS3TicketUploads } from '@/features/ticket/components/TicketDetailsDialog/hooks/useS3TicketUploads';
import { usePhotoUploads } from '../hooks/usePhotoUploads';

export interface StagedFile {
  id: string;
  file: File;
  previewUrl: string;
}

interface PhotoUploadProps {
  initialPhotos: IUpload[];
  ticketId?: string;
  canEdit: boolean;
}

export const PhotoUploadTab = ({
  initialPhotos,
  ticketId,
  canEdit,
}: PhotoUploadProps) => {
  const { user } = useAuth();
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const {
    setUploadedPhotos,
    uploadedPhotos,
    stagedFiles,
    handleNewFiles: onNewFiles,
    handleRemoveStagedFile: onRemoveStagedFile,
    handleUploadComplete: onUploadComplete,
  } = usePhotoUploads();

  const [allPhotos, setAllPhotos] = useState<IUpload[]>(initialPhotos);

  useEffect(() => {
    setAllPhotos(initialPhotos);
  }, [initialPhotos]);

  // Upload to S3
  const { uploadMutation, viewPhotoMutation, deletePhotoMutation } =
    useS3TicketUploads(ticketId!);

  // --- Event Handlers ---
  const handleFileSelection = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) onNewFiles(Array.from(e.target.files));
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files) onNewFiles(Array.from(e.dataTransfer.files));
  };

  const handleFinalUpload = async () => {
    if (stagedFiles.length === 0) return;

    const uploadPromises = stagedFiles.map((sf) =>
      uploadMutation.mutateAsync(sf),
    );

    // Use Promise.allSettled to handle individual failures gracefully without stopping the batch.
    const results = await Promise.allSettled(uploadPromises);

    const successfulUploads: IUpload[] = [];
    let failureCount = 0;

    results.forEach((res, index) => {
      if (res.status === 'fulfilled') {
        const originalFile = stagedFiles[index];
        successfulUploads.push({
          id: res.value.id, // Using the temporary ID from the staged file
          s3Key: res.value.viewUrl,
          filename: res.value.file.name,
          mimetype: res.value.file.type,
          size: res.value.file.size,
          uploadedAt: new Date().toISOString(),
          uploadedById: user!.id,
          ticketId: ticketId,
        });
      } else {
        failureCount++;
      }
    });

    if (successfulUploads.length > 0) {
      // Update local state to immediately show new photos in the UI.
      setAllPhotos((prevPhotos) => [...prevPhotos, ...successfulUploads]);
      onUploadComplete(successfulUploads); // Notify parent component
      toast.success(
        `${successfulUploads.length} photo(s) uploaded successfully!`,
      );
    }

    if (failureCount > 0) {
      toast.warning(
        `${failureCount} photo(s) failed to upload. Please try them again.`,
      );
    }
  };

  const handleViewPhoto = (uploadId: string) => {
    viewPhotoMutation.mutate(uploadId);
  };

  const handleDeletePhoto = (uploadId: string) => {
    deletePhotoMutation.mutate(uploadId);
  };

  if (!canEdit) {
    return (
      <div className="pt-4">
        {allPhotos.length > 0 ? (
          allPhotos.map((photo) => (
            <div
              key={photo.id}
              className="flex items-center justify-between p-2 hover:bg-muted/50 rounded-md transition-colors"
            >
              <div className="flex items-center gap-3 overflow-hidden">
                <FileImage className="h-6 w-6 flex-shrink-0 text-muted-foreground" />
                <div className="flex flex-col truncate">
                  <span className="text-sm font-medium truncate">
                    {photo.filename}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {`(${(photo.size / 1024).toFixed(
                      1,
                    )} KB) • Uploaded on ${new Date(
                      photo.uploadedAt,
                    ).toLocaleDateString()}`}
                  </span>
                </div>
              </div>

              <Button
                variant="outline"
                size="sm"
                className="ml-4 flex-shrink-0"
                type="button"
                onClick={() => handleViewPhoto(photo.id)}
                disabled={
                  viewPhotoMutation.isPending &&
                  viewPhotoMutation.variables === photo.id
                }
              >
                {viewPhotoMutation.isPending &&
                viewPhotoMutation.variables === photo.id ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  'View'
                )}
                <Eye className="ml-2 h-4 w-4" />
              </Button>
            </div>
          ))
        ) : (
          <div className="text-center text-muted-foreground">
            No photos uploaded.
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div
        className={`rounded-md border-2 border-dashed p-6 text-center transition-colors cursor-pointer ${
          isDragging ? 'border-primary bg-muted/50' : ''
        }`}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <Upload className="mx-auto h-12 w-12 text-muted-foreground" />
        <p className="mt-1 text-sm text-muted-foreground">
          Drag and drop, browse files, or take a photo
        </p>
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept="image/*"
          multiple
          onChange={handleFileSelection}
        />
      </div>

      {/* --- Staging Area --- */}
      {stagedFiles.length > 0 && (
        <div className="space-y-3 rounded-md border p-4">
          <div className="flex justify-between items-center">
            <h4 className="text-sm font-medium">
              Staged for Upload ({stagedFiles.length})
            </h4>
            <Button
              size="sm"
              type="button"
              onClick={(e) => {
                e.preventDefault();
                handleFinalUpload();
              }}
              disabled={uploadMutation.isPending}
            >
              {uploadMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Upload className="mr-2 h-4 w-4" />
              )}
              Upload All
            </Button>
          </div>
          <div className="space-y-2">
            {stagedFiles.map((sf) => (
              <div
                key={sf.id}
                className="flex items-center justify-between rounded-md border bg-muted/50 p-2 text-sm"
              >
                <div className="flex items-center gap-2 overflow-hidden">
                  <FileImage className="h-5 w-5 flex-shrink-0 text-muted-foreground" />
                  <span className="truncate font-medium">{sf.file.name}</span>
                  <span className="text-muted-foreground text-xs">
                    {' '}
                    ({(sf.file.size / 1024).toFixed(1)} KB)
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    title="Preview"
                    size="icon"
                    type="button"
                    variant="ghost"
                    className="h-7 w-7"
                    onClick={(e) => {
                      e.preventDefault();
                      window.open(sf.previewUrl, '_blank');
                    }}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                  <a
                    title="Download"
                    href={sf.previewUrl}
                    download={sf.file.name}
                    className="inline-flex items-center justify-center h-7 w-7"
                  >
                    <Download className="h-4 w-4" />
                  </a>
                  <Button
                    title="Remove"
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/50"
                    onClick={() => onRemoveStagedFile(sf.id)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* --- Previously Uploaded Photos --- */}
      {allPhotos.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-medium">Uploaded Files</h4>
          <div className="flex flex-col gap-2 rounded-md border p-2">
            {allPhotos.map((photo) => (
              <div
                key={photo.id}
                className="flex items-center justify-between p-2 hover:bg-muted/50 rounded-md transition-colors"
              >
                <div className="flex items-center gap-3 overflow-hidden">
                  <FileImage className="h-6 w-6 flex-shrink-0 text-muted-foreground" />
                  <div className="flex flex-col truncate">
                    <span className="text-sm font-medium truncate">
                      {photo.filename}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {`(${(photo.size / 1024).toFixed(
                        1,
                      )} KB) • Uploaded on ${new Date(
                        photo.uploadedAt,
                      ).toLocaleDateString()}`}
                    </span>
                  </div>
                </div>

                <div>
                  <Button
                    variant="default"
                    size="sm"
                    className="ml-4 flex-shrink-0"
                    type="button"
                    onClick={() => handleViewPhoto(photo.id)}
                    disabled={
                      viewPhotoMutation.isPending &&
                      viewPhotoMutation.variables === photo.id
                    }
                  >
                    {viewPhotoMutation.isPending &&
                    viewPhotoMutation.variables === photo.id ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      'View'
                    )}
                    <Eye className="ml-2 h-4 w-4" />
                  </Button>
                  {/* TODO: Only admins and user who uploaded file can delete this file */}
                  {canEdit && (
                    <Button
                      variant="destructive"
                      size="sm"
                      className="ml-4 flex-shrink-0"
                      type="button"
                      onClick={() => handleDeletePhoto(photo.id)}
                      disabled={
                        deletePhotoMutation.isPending &&
                        deletePhotoMutation.variables === photo.id
                      }
                    >
                      {deletePhotoMutation.isPending &&
                      deletePhotoMutation.variables === photo.id ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        'Delete'
                      )}
                      <X className="ml-2 h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
